import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Badge, { badgeClasses } from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../redux/authSlice'
import { Link as RouterLink } from 'react-router-dom'
import withAlert from '../hoc/withAlerts'
import Loading from './pages/parts/Loading'
import Breadcrumbs from './pages/parts/Breadcrumbs';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FormLabel from '@mui/material/FormLabel';
import Confirmation from './pages/modals/Confirmation';
import SearchBox from './pages/parts/SearchBox';
import endpoints, { getBackendUrl } from '../endpoints';
import axios from 'axios';
import { ROLES, checkPermissions } from '../roles'
import { tokens } from '../theme';

const lightColor = 'rgba(255, 255, 255, 0.7)';

const rightMenuProps = {
    elevation: 0,
    sx: {
        filter: 'drop-shadow(0px 8px 24px rgba(20, 22, 31, 0.16))',
        border: '1px solid ' + tokens.line,
        borderRadius: '12px',
        mt: 0.5,
        minWidth: 160,
        maxWidth: 400,
        maxHeight: '90vh',
        '& .MuiListItemIcon-root': {
            minWidth: '20px!important',
        },
        '& .MuiAvatar-root': {
            width: 24,
            height: 24,
            ml: -0.5,
            mr: 0.25,
            minWidth: 'auto',
        },
        '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
        },
    },
}

function Header(props) {
    const { onDrawerToggle } = props;
    
    const role = useSelector((state) => state.auth.userdata.role)
    const [loading, setLoading] = useState(false)
    
    const [anchorAvatar, setAnchorAvatar] = useState(null)    
    const handleAvatarClick = (event) => {
        setAnchorAvatar(event.currentTarget);
    };
    const handleAvatarClose = () => {
        setAnchorAvatar(null);
    };
    
    const [anchorNotify, setAnchorNotify] = useState(null)    
    const handleNotifyClick = (event) => {
        setAnchorNotify(event.currentTarget);
    };
    const handleNotifyClose = () => {
        setAnchorNotify(null);
    };

    const dispatch = useDispatch()
    const token = useSelector((state) => state.auth.token)
    const email = useSelector((state) => state.auth.userdata.email)
    const firstname = useSelector((state) => state.auth.userdata.firstname)
    const lastname = useSelector((state) => state.auth.userdata.lastname)
    const avatar = useSelector((state) => state.auth.userdata.avatar_url)
    
    const [notifications, setNotifications] = useState()
    
    const header = useSelector((state) => state.header.data)
    
    let letter = ''

    if (email) {
        letter = email.charAt(0).toUpperCase();
    }
    
    const showAlertError = (message) => {
        props.showAlert(message, 'error')
    }

    const showAlertSuccess = (message) => {
        props.showAlert(message, 'success')
    }
    
    const getNotifications = async () => {
        const uri = getBackendUrl(endpoints.notifications, token)
        try {
            const res = await axios.get(uri)
            setNotifications(res.data.items)
        } catch (e) {
            showAlertError('Unknown Error - get notification list')
        }
    }
    
    const contentsUpdatedHandler = (event) => {
        const updated = event.detail || []
        
        setNotifications((prev) => {
            return prev.filter((item) => {
                return !updated.find((a) => a.id === item.id)
            })
        })
    }
    
    const suitableFoundHandler = (event) => {
        getNotifications()
    }
    
    useEffect(() => {
        getNotifications()
        
        window.addEventListener('CONTENTS_UPDATED', contentsUpdatedHandler)
        window.addEventListener('SUITABLE_FOUND', suitableFoundHandler)
        return () => {
            window.removeEventListener('CONTENTS_UPDATED', contentsUpdatedHandler)
            window.removeEventListener('SUITABLE_FOUND', suitableFoundHandler)
        }
    }, [])

    return (
        <React.Fragment>
            <AppBar
                component="div"
                position="fixed"
                elevation={0}
                sx={{
                    zIndex: 100,
                    padding: 0,
                    margin: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.82)',
                    backdropFilter: 'saturate(180%) blur(12px)',
                    WebkitBackdropFilter: 'saturate(180%) blur(12px)',
                    color: 'text.primary',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Toolbar sx={{ padding: '0!important', mr: -2 }}>
                    <Stack sx={{ width: '100%', pl: 0 }}>
                        <Stack 
                            direction="row" 
                            alignItems="center" 
                            spacing={1}
                            sx={{ width: '100%', pt: 1, pb: 1, pr: 4 }}
                        >
                            <Stack sx={{width: props.navigatorWidth, display: { sm: 'block', xs: 'none' } }}/>
                            <Stack 
                                direction="row" 
                                spacing={2} 
                                alignItems="center" 
                                sx={{flexGrow:1, minWidth:0, pl:3, pr:2, pt:0.32, pb:0.25, minHeight:41, whiteSpace: 'nowrap', overflowX: 'hidden',}}>
                                <Typography color="inherit" variant="h5" component="h1" sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                    {header?.title ?? ''}
                                </Typography>
                                {header?.title ?
                                <Stack sx={{flexGrow:1}}>
                                    {header?.content}
                                </Stack>
                                : null }
                            </Stack>
                            <Stack sx={{pl: 2, display: { sm: 'none', xs: 'block' } }}>
                                <IconButton
                                  color="inherit"
                                  aria-label="open drawer"
                                  onClick={onDrawerToggle}
                                  edge="start"
                                >
                                  <MenuRoundedIcon />
                                </IconButton>
                            </Stack>
                            <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                                {checkPermissions(props.authuserrole, ROLES.ROLE_USER) ? <SearchBox width={400} /> : null }
                                <Tooltip arrow title="Alerts">
                                    <IconButton color="inherit" onClick={handleNotifyClick}>
                                        <Badge
                                            badgeContent={notifications?.length ?? 0}
                                            color="error"
                                            overlap="circular"
                                            sx={{
                                                '& .MuiBadge-badge': {
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    minWidth: 16,
                                                    height: 16,
                                                    border: '2px solid #fff',
                                                },
                                            }}
                                        >
                                            <NotificationsNoneRoundedIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={anchorNotify}
                                    open={anchorNotify ? true : false}
                                    onClose={handleNotifyClose}
                                    onClick={handleNotifyClose}
                                    PaperProps={rightMenuProps}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    {notifications?.length ?
                                    notifications.map((row) => {
                                        return (
                                            <MenuItem 
                                                component={RouterLink}
                                                sx={{whiteSpace: 'normal'}} 
                                                key={row.id} 
                                                to={'/feeds/contents/' + row.feed_id + '?startId=' + row.id}
                                            >
                                                <ListItemText primary={row.title} secondary={row.feed_name}/>
                                            </MenuItem>
                                        )
                                    }) :
                                    <MenuItem>
                                        <ListItemText>
                                        No Notifications
                                        </ListItemText>
                                    </MenuItem>
                                    }
                                </Menu>
                                
                                <Tooltip arrow title="Account">
                                    <IconButton color="inherit" onClick={handleAvatarClick} sx={{my:-10}}>
                                        <Avatar
                                            src={avatar ?? undefined}
                                            alt={letter}
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 15,
                                                bgcolor: tokens.accentSoft,
                                                color: tokens.accent,
                                                border: '1px solid ' + tokens.line,
                                            }}
                                        >
                                            {letter}
                                        </Avatar>
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={anchorAvatar}
                                    open={anchorAvatar ? true : false}
                                    onClose={handleAvatarClose}
                                    onClick={handleAvatarClose}
                                    PaperProps={rightMenuProps}
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                >
                                    <MenuItem component={RouterLink} to="/me">
                                        <ListItemIcon>
                                            <Avatar
                                                src={avatar ?? undefined}
                                                sx={{ bgcolor: tokens.accentSoft, color: tokens.accent }}
                                            >
                                                {letter}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText primary="Profile" />
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem onClick={() => {
                                        // Full logout: clear local state, revoke token on backend + clear httpOnly cookie.
                                        // Must hit the backend origin — frontend /logout only re-routes to Login,
                                        // which then auto-logs the user back in via the still-valid token.
                                        const logoutToken = token
                                        dispatch(logout())
                                        window.location.assign(getBackendUrl('/logout', logoutToken))
                                    }}>
                                        <ListItemIcon>
                                            <LogoutRoundedIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary="Logout" />
                                    </MenuItem>
                                </Menu>
                            </Stack>
                            
                        </Stack>
                    </Stack>
                </Toolbar>
            </AppBar>
            <AppBar
                component="div" position="static" elevation={0}
                sx={{ zIndex: 0, bgcolor: 'background.paper', color: 'text.primary' }}
            >
            </AppBar>
            {loading ? <Loading/> : null}
        </React.Fragment>
    );
}

Header.propTypes = {
    onDrawerToggle: PropTypes.func.isRequired,
};

export default withAlert(Header);
